import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Save, Loader2, History, Plus, ArrowRightLeft, ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { parseTalla, compareBySku } from "../lib/talla";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * <EditInventoryModal product={...} onClose={} onSaved={} />
 * Lists all (source, location, sku) rows for a given internal product and
 * lets the user set an absolute new quantity per row. Each save hits
 * POST /inventory-write/adjust.
 */
export const EditInventoryModal = ({ product, onClose, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // { rowKey: string }
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [filter, setFilter] = useState("");
  const [odooLocations, setOdooLocations] = useState([]);
  const [shopifyLocations, setShopifyLocations] = useState([]);
  // sku -> selected location_id for the "Agregar ubicación" picker
  const [addLocSelection, setAddLocSelection] = useState({});
  // Transfer dialog state
  const [transferRow, setTransferRow] = useState(null);
  const [transferDest, setTransferDest] = useState("");  // "platform:location_id"
  const [transferQty, setTransferQty] = useState("");
  const [transferring, setTransferring] = useState(false);
  // Which SKU cards are expanded to show per-location detail. Default: all collapsed.
  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (sku) => setExpanded((p) => ({ ...p, [sku]: !p[sku] }));
  const [expectedSkus, setExpectedSkus] = useState([]);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const rowKey = (r) => `${r.source}:${r.location_id}:${r.sku}`;

  useEffect(() => {
    if (!product) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/inventory-write/product/${product.internal_product_id}`, auth());
        setRows(res.data.rows || []);
        setOdooLocations(res.data.odoo_locations || []);
        setShopifyLocations(res.data.shopify_locations || []);
        setExpectedSkus(res.data.expected_skus || []);
        const d = {};
        (res.data.rows || []).forEach((r) => { d[rowKey(r)] = String(Number(r.available || 0)); });
        setDrafts(d);
        // If there's only ONE unique SKU (i.e. product without variants), auto-expand it
        // so users see the per-location rows without needing to tap. Includes
        // expected SKUs so a product with zero stock everywhere still opens up
        // ready for adding the first location.
        const skuSet = new Set([
          ...(res.data.rows || []).map((r) => r.sku),
          ...(res.data.expected_skus || []),
        ]);
        const uniqueSkus = Array.from(skuSet);
        if (uniqueSkus.length === 1) {
          setExpanded({ [uniqueSkus[0]]: true });
        } else {
          setExpanded({});
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al cargar ubicaciones");
      } finally {
        setLoading(false);
      }
    })();
  }, [product]);

  const loadHistory = async () => {
    if (!product) return;
    try {
      const r = await axios.get(`${API}/inventory-write/history?internal_product_id=${product.internal_product_id}&limit=50`, auth());
      setHistory(r.data || []);
      setShowHistory(true);
    } catch (e) {
      toast.error("Error al cargar historial");
    }
  };

  const save = async (row) => {
    const key = rowKey(row);
    const val = drafts[key];
    if (val === undefined || val === "") { toast.info("Escribe una cantidad"); return; }
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) { toast.error("Cantidad inválida"); return; }
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      await axios.post(`${API}/inventory-write/adjust`, {
        platform: row.source,
        location_id: String(row.location_id),
        sku: row.sku,
        new_qty: n,
        inventory_item_id: row.inventory_item_id || null,
        internal_product_id: product.internal_product_id,
      }, auth());
      toast.success(`${row.sku} en ${row.location_name}: ${row.available} → ${n}`);
      // Update local row so re-open reflects it
      setRows((prev) => prev.map((r) => rowKey(r) === key ? { ...r, available: n } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ajustar");
    } finally {
      setSaving((p) => ({ ...p, [key]: false }));
    }
  };

  const openTransfer = (row) => {
    setTransferRow(row);
    setTransferDest("");
    setTransferQty("1");
  };

  const doTransfer = async () => {
    if (!transferRow || !transferDest) return;
    const [destPlat, destLocId] = transferDest.split(":");
    const qty = Number(transferQty);
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Cantidad inválida"); return; }
    if (qty > Number(transferRow.available || 0)) {
      toast.error(`Solo hay ${transferRow.available} en origen`);
      return;
    }

    // Find the inventory_item_id for the destination row if it's shopify
    let destInvItemId = null;
    if (destPlat === "shopify") {
      const existing = rows.find(
        (r) => r.source === "shopify" && String(r.location_id) === String(destLocId) && r.sku === transferRow.sku
      );
      if (existing?.inventory_item_id) destInvItemId = existing.inventory_item_id;
      else {
        // Fallback: use any shopify row with same SKU to get the inventory_item_id
        const anyShopify = rows.find((r) => r.source === "shopify" && r.sku === transferRow.sku && r.inventory_item_id);
        destInvItemId = anyShopify?.inventory_item_id || null;
        if (!destInvItemId) {
          toast.error("No pude encontrar el inventory_item_id de Shopify para este SKU");
          return;
        }
      }
    }

    setTransferring(true);
    try {
      const res = await axios.post(`${API}/inventory-write/transfer`, {
        sku: transferRow.sku,
        qty,
        source_platform: transferRow.source,
        source_location_id: String(transferRow.location_id),
        source_inventory_item_id: transferRow.inventory_item_id || null,
        dest_platform: destPlat,
        dest_location_id: String(destLocId),
        dest_inventory_item_id: destInvItemId,
        internal_product_id: product.internal_product_id,
      }, auth());
      const info = res.data.odoo_picking_ref ? ` (Odoo ${res.data.odoo_picking_ref})` : "";
      toast.success(`Transferido ${qty} de ${transferRow.sku}${info}`);
      // Refresh rows from server so both sides show up right
      const fresh = await axios.get(`${API}/inventory-write/product/${product.internal_product_id}`, auth());
      setRows(fresh.data.rows || []);
      const d = {};
      (fresh.data.rows || []).forEach((r) => { d[rowKey(r)] = String(Number(r.available || 0)); });
      setDrafts(d);
      setTransferRow(null);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al transferir");
    } finally {
      setTransferring(false);
    }
  };

  if (!product) return null;

  // Filter rows by SKU or location name (case-insensitive)
  const f = filter.trim().toLowerCase();
  const filteredRows = f
    ? rows.filter((r) =>
        (r.sku || "").toLowerCase().includes(f) ||
        (r.location_name || "").toLowerCase().includes(f)
      )
    : rows;

  // Group filtered rows by SKU for cleaner presentation.
  // Also include SKUs that have ZERO rows yet (from expected_skus) so users
  // can add the very first location for a product that never had stock.
  const groups = filteredRows.reduce((acc, r) => {
    if (!acc[r.sku]) acc[r.sku] = [];
    acc[r.sku].push(r);
    return acc;
  }, {});
  // Add empty buckets for expected SKUs when no filter is active (filtering
  // by text should not surface unrelated empty SKUs).
  if (!filter) {
    expectedSkus.forEach((sku) => {
      if (!groups[sku]) groups[sku] = [];
    });
  }

  return (
    <Dialog open={!!product} onOpenChange={(open) => { if (!open) { onClose(); setShowHistory(false); } }}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-auto bg-white max-h-[85vh] overflow-y-auto p-4 sm:p-6" data-testid="edit-inventory-modal">
        <DialogHeader>
          <DialogTitle className="text-[#4A4A4A] font-['Manrope'] text-base sm:text-lg">
            Editar inventario · {product.internal_product_name}
          </DialogTitle>
          <DialogDescription className="text-[#6B7280] text-xs sm:text-sm">
            Toca una talla para ver el desglose por ubicación. Al Guardar actualizas el stock <b>directo en Odoo/Shopify</b>. Usa <span className="inline-flex items-center gap-1"><ArrowRightLeft className="w-3 h-3 text-[#3B82F6]" /></span> para mover piezas entre ubicaciones.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 mb-2">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar por SKU o ubicación..."
            className="flex-1 h-9 bg-white border-[#FECDD3] text-sm"
            data-testid="inv-modal-search"
          />
          <Button size="sm" variant="ghost" onClick={loadHistory} className="text-[#6B7280] flex-shrink-0" data-testid="load-inventory-history">
            <History className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Historial</span>
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-[#6B7280]"><Loader2 className="animate-spin inline w-4 h-4 mr-2" />Cargando ubicaciones...</div>
        ) : Object.keys(groups).length === 0 ? (
          <p className="text-center text-[#6B7280] py-8 text-sm">
            {rows.length === 0
              ? "No hay ubicaciones registradas para este producto. Corre \"Sincronizar Inventario\" primero."
              : `Ningún SKU o ubicación matchea "${filter}".`}
          </p>
        ) : (
          <div className="space-y-2">
            {Object.entries(groups)
              .sort(([a], [b]) => compareBySku(a, b))
              .map(([sku, list]) => {
              const usedOdooLocIds = new Set(
                list.filter((r) => r.source === "odoo").map((r) => String(r.location_id))
              );
              const usedShopifyLocIds = new Set(
                list.filter((r) => r.source === "shopify").map((r) => String(r.location_id))
              );
              const missingOdooLocations = odooLocations.filter(
                (l) => !usedOdooLocIds.has(String(l.id))
              );
              const missingShopifyLocations = shopifyLocations.filter(
                (l) => !usedShopifyLocIds.has(String(l.id))
              );
              // Combined picker options: value is "platform:id"
              const missingCombined = [
                ...missingOdooLocations.map((l) => ({ ...l, platform: "odoo" })),
                ...missingShopifyLocations.map((l) => ({ ...l, platform: "shopify" })),
              ];
              const selectedNewLoc = addLocSelection[sku] || "";
              const { talla } = parseTalla(sku);
              const totalQty = list.reduce((sum, r) => sum + Number(r.available || 0), 0);
              const isOpen = !!expanded[sku];
              // Also compute total per platform for the compact view
              const totalOdoo = list.filter(r => r.source === "odoo").reduce((s, r) => s + Number(r.available || 0), 0);
              const totalShopify = list.filter(r => r.source === "shopify").reduce((s, r) => s + Number(r.available || 0), 0);
              return (
              <div key={sku} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                {/* COMPACT HEADER — always visible */}
                <button
                  type="button"
                  onClick={() => toggleExpanded(sku)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#FEF2F2]/50 transition-colors text-left"
                  data-testid={`sku-card-header-${sku}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />}
                  {talla ? (
                    <span className="inline-flex items-center justify-center min-w-[42px] h-8 px-2 rounded-md bg-gradient-to-br from-[#F43F5E] to-[#E11D48] text-white text-sm font-bold shadow-sm">
                      {talla}
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center min-w-[42px] h-8 px-2 rounded-md bg-gray-100 text-gray-500 text-[10px] font-medium">
                      —
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] sm:text-xs text-[#6B7280] truncate">{sku}</div>
                    <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] mt-0.5">
                      {totalOdoo > 0 && <span><span className="text-[#8B5CF6] font-medium">Odoo</span> {totalOdoo.toFixed(0)}</span>}
                      {totalOdoo > 0 && totalShopify > 0 && <span>·</span>}
                      {totalShopify > 0 && <span><span className="text-[#10B981] font-medium">Shopify</span> {totalShopify.toFixed(0)}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[9px] text-[#9CA3AF] uppercase tracking-wide leading-none">Total</div>
                    <div className="text-lg font-bold text-[#4A4A4A] leading-tight">{totalQty.toFixed(0)}</div>
                  </div>
                </button>

                {/* EXPANDED DETAIL — per-location editable rows */}
                {isOpen && (
                <div className="border-t border-[#F3F4F6] bg-[#FEF7F7]/30 p-3">
                <div className="space-y-2">
                  {list.map((r) => {
                    const key = rowKey(r);
                    return (
                      <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm p-2 rounded bg-white border border-[#F3F4F6]">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Badge variant="outline" className={r.source === "odoo" ? "border-[#8B5CF6] text-[#8B5CF6]" : "border-[#10B981] text-[#10B981]"}>
                            {r.source}
                          </Badge>
                          <span className="flex-1 text-[#4A4A4A] truncate">{r.location_name}</span>
                          <span className="text-xs text-[#9CA3AF] whitespace-nowrap">actual: {Number(r.available).toFixed(0)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            className="flex-1 sm:w-24 h-8"
                            type="number"
                            value={drafts[key] ?? ""}
                            onChange={(e) => setDrafts((p) => ({ ...p, [key]: e.target.value }))}
                            data-testid={`inv-input-${key}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => save(r)}
                            disabled={saving[key] || String(drafts[key]) === String(r.available)}
                            className="bg-[#F43F5E] hover:bg-[#E11D48] text-white h-8"
                            title="Actualizar stock directo en Odoo/Shopify"
                            data-testid={`inv-save-${key}`}
                          >
                            {saving[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Guardar</span></>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openTransfer(r)}
                            disabled={Number(r.available || 0) <= 0}
                            className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 h-8 px-2"
                            title="Mover piezas a otra ubicación"
                            data-testid={`inv-transfer-${key}`}
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {missingCombined.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1 p-2 rounded border border-dashed border-[#8B5CF6]/40 bg-[#8B5CF6]/5">
                      <span className="text-[10px] text-[#6B7280] uppercase tracking-wide sm:mr-1" title="Agregar una ubicación de Odoo o Shopify donde este SKU nunca ha tenido stock">Agregar ubicación</span>
                      <select
                        value={selectedNewLoc}
                        onChange={(e) => setAddLocSelection((p) => ({ ...p, [sku]: e.target.value }))}
                        className="flex-1 h-8 text-xs bg-white border border-[#E5E7EB] rounded px-2"
                        data-testid={`add-loc-select-${sku}`}
                      >
                        <option value="">Selecciona ubicación…</option>
                        {missingOdooLocations.length > 0 && (
                          <optgroup label="Odoo">
                            {missingOdooLocations.map((l) => (
                              <option key={`odoo-${l.id}`} value={`odoo:${l.id}`}>{l.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {missingShopifyLocations.length > 0 && (
                          <optgroup label="Shopify">
                            {missingShopifyLocations.map((l) => (
                              <option key={`shopify-${l.id}`} value={`shopify:${l.id}`}>{l.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!selectedNewLoc}
                        onClick={() => {
                          const [platform, id] = selectedNewLoc.split(":");
                          const src = platform === "odoo" ? odooLocations : shopifyLocations;
                          const loc = src.find((l) => String(l.id) === String(id));
                          if (!loc) return;
                          const newRow = {
                            sku,
                            source: platform,
                            location_id: String(loc.id),
                            location_name: loc.name,
                            available: 0,
                          };
                          setRows((prev) => [...prev, newRow]);
                          setDrafts((p) => ({ ...p, [rowKey(newRow)]: "0" }));
                          setAddLocSelection((p) => ({ ...p, [sku]: "" }));
                          setExpanded((p) => ({ ...p, [sku]: true }));
                        }}
                        className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10 h-8"
                        data-testid={`add-loc-btn-${sku}`}
                      >
                        <Plus className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Agregar</span>
                      </Button>
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {showHistory && (
          <div className="mt-4 border-t border-[#E5E7EB] pt-3">
            <h4 className="text-sm font-semibold text-[#4A4A4A] mb-2">Historial de ajustes</h4>
            {history.length === 0 ? (
              <p className="text-xs text-[#6B7280]">Sin ajustes registrados aún.</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="text-xs text-[#4A4A4A] border-b border-[#F3F4F6] py-1.5 flex flex-wrap gap-2">
                    <span className="text-[#6B7280]">{new Date(h.timestamp).toLocaleString("es-MX")}</span>
                    <Badge variant="outline" className={h.platform === "odoo" ? "border-[#8B5CF6] text-[#8B5CF6] text-[10px]" : "border-[#10B981] text-[#10B981] text-[10px]"}>
                      {h.platform}
                    </Badge>
                    <code className="font-mono">{h.sku}</code>
                    <span>loc {h.location_id}:</span>
                    <span className="font-medium">
                      {h.previous_qty ?? "?"} → <span className="text-[#F43F5E]">{h.new_qty}</span>
                    </span>
                    {h.transfer_id && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        h.direction === "out" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
                      }`}>
                        transfer · {h.direction === "out" ? "sale" : "entra"}
                      </span>
                    )}
                    <span className="text-[#9CA3AF] ml-auto">{h.user_email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transfer mini-dialog */}
        {transferRow && (() => {
          const destOptions = [
            ...odooLocations
              .filter((l) => !(transferRow.source === "odoo" && String(l.id) === String(transferRow.location_id)))
              .map((l) => ({ value: `odoo:${l.id}`, label: `odoo · ${l.name}` })),
            ...shopifyLocations
              .filter((l) => !(transferRow.source === "shopify" && String(l.id) === String(transferRow.location_id)))
              .map((l) => ({ value: `shopify:${l.id}`, label: `shopify · ${l.name}` })),
          ];
          return (
            <Dialog open={true} onOpenChange={(o) => { if (!o) setTransferRow(null); }}>
              <DialogContent className="max-w-md bg-white p-4 sm:p-6" data-testid="transfer-dialog">
                <DialogHeader>
                  <DialogTitle className="text-[#4A4A4A] font-['Manrope'] text-base sm:text-lg flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4 text-[#3B82F6]" /> Transferir inventario
                  </DialogTitle>
                  <DialogDescription className="text-[#6B7280] text-xs sm:text-sm">
                    Mueve piezas de un almacén/canal a otro. Odoo↔Odoo crea una transferencia interna nativa; los demás casos hacen dos ajustes atómicos.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-2">
                  <div className="flex items-center gap-2 text-sm p-2 rounded bg-[#F3F4F6]">
                    <code className="font-mono text-xs">{transferRow.sku}</code>
                    <span className="text-[#6B7280]">·</span>
                    <Badge variant="outline" className={transferRow.source === "odoo" ? "border-[#8B5CF6] text-[#8B5CF6]" : "border-[#10B981] text-[#10B981]"}>
                      {transferRow.source}
                    </Badge>
                    <span className="text-[#4A4A4A] truncate">{transferRow.location_name}</span>
                    <span className="text-xs text-[#9CA3AF] ml-auto">actual: {Number(transferRow.available).toFixed(0)}</span>
                  </div>

                  <div className="flex items-center justify-center text-[#9CA3AF]">
                    <ArrowRight className="w-4 h-4" />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-[#6B7280]">Destino</label>
                    <select
                      value={transferDest}
                      onChange={(e) => setTransferDest(e.target.value)}
                      className="w-full h-9 text-sm bg-white border border-[#E5E7EB] rounded px-2"
                      data-testid="transfer-dest-select"
                    >
                      <option value="">Selecciona ubicación destino…</option>
                      {destOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-[#6B7280]">Cantidad</label>
                    <Input
                      type="number"
                      min="1"
                      max={transferRow.available}
                      value={transferQty}
                      onChange={(e) => setTransferQty(e.target.value)}
                      className="h-9 bg-white border-[#E5E7EB]"
                      data-testid="transfer-qty-input"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setTransferRow(null)} className="flex-1" disabled={transferring}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={doTransfer}
                      disabled={!transferDest || !transferQty || transferring}
                      className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                      data-testid="transfer-confirm-btn"
                    >
                      {transferring ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transfiriendo…</>
                      ) : (
                        <><ArrowRightLeft className="w-4 h-4 mr-1" /> Transferir</>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}

        <div className="flex justify-end mt-3">
          <Button variant="outline" onClick={() => { onSaved(); }} data-testid="close-inventory-modal">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditInventoryModal;
